/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelectedOrganizationId, useAuthtoken } from 'stores/useAuthStore';
import { useQuery } from '@tanstack/react-query';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

// MUI Components
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid';

// Types
import type { Configuration } from 'containers/Schema/schema.types';

import {
  baseViewListQueryOptions,
  type BaseViewParams,
} from 'containers/Base/useBaseViewData';

type Props = {
  className: any;
  configuration: Configuration;
  defaultValueSelector?: (data: any) => any;
  disabled?: boolean;
  helperText?: string;
  idColumn: string;
  label: string;
  labelColumns?: string[];
  onChange: (e: any, value: any) => void;
  searchColumn?: string;
  size?: 'small' | 'medium';
  useGlobalSearch?: boolean; // requires listViewColumns in configuration to know which columns to search
  value: any;
};

export function AutocompleteField({
  className,
  configuration,
  defaultValueSelector,
  disabled,
  helperText,
  idColumn,
  label,
  labelColumns,
  onChange,
  searchColumn,
  size = 'medium',
  useGlobalSearch = false,
  value,
}: Props) {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  // State
  const [paginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [debounceTimer, setDebounceTimer] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [defaultValueApplied, setDefaultValueApplied] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    undefined
  );
  const [globalSearch, setGlobalSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const params: BaseViewParams | null =
    filterModel === undefined
      ? null
      : {
          disablePagination: false,
          forceRefresh: false,
          filterModel,
          globalSearch,
          paginationModel,
          sortModel,
        };

  const listQueryOptions = baseViewListQueryOptions(
    configuration,
    params,
    authToken,
    organizationId
  );
  const listQuery = useQuery({
    ...listQueryOptions,
    enabled: !disabled && listQueryOptions.enabled,
  });
  const loading = listQuery.isFetching;

  // Misc
  const filterModelChanged = (newFilterModel: GridFilterModel) => {
    let changed = false;
    if (newFilterModel.items.length !== filterModel?.items.length) {
      changed = true;
    } else {
      for (let i = 0; i < newFilterModel.items.length; i++) {
        const newFilterItem = newFilterModel.items[i];
        const existingFilterItem = filterModel.items.find(
          (x) => x.field === newFilterItem.field
        );
        if (!existingFilterItem) {
          changed = true;
          break;
        } else if (
          existingFilterItem.value !== newFilterItem.value ||
          existingFilterItem.operator !== newFilterItem.operator
        ) {
          changed = true;
          break;
        }
      }
    }
    return changed;
  };

  const basefilterModelItems = useMemo(() => {
    const items: GridFilterModel['items'] = [];

    if (configuration.defaultFilterModel && !value && !inputValue) {
      items.push(...configuration.defaultFilterModel.items);
    }
    return items;
  }, [configuration.defaultFilterModel, value, inputValue]);

  // Handlers
  const handleInputChange = (
    _e: React.ChangeEvent<any>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    setDebounceTimer(
      setTimeout(async () => {
        // Build out a filterModel with a startsWith operator on the idColumn
        // and then kick off a refresh of the data
        const newFilterModel: GridFilterModel = {
          items: basefilterModelItems,
        };
        const useIdColumn = newInputValue.includes('|');
        const filterValue = useIdColumn
          ? newInputValue.split('|')[0].trim()
          : newInputValue;

        if (
          useGlobalSearch &&
          !useIdColumn &&
          // must set listViewColumns in order to use global search
          configuration.listViewColumns?.length
        ) {
          setGlobalSearch(newInputValue);
        } else {
          setGlobalSearch('');
          newFilterModel.items.push({
            field: (useIdColumn ? idColumn : searchColumn) || idColumn,
            operator: 'contains',
            value: filterValue,
          });
        }

        if (filterModelChanged(newFilterModel)) {
          setFilterModel(newFilterModel);
        }
      }, 500)
    );
  };

  useEffect(() => {
    const newFilterModel: GridFilterModel = {
      items: basefilterModelItems,
    };
    if (value?.id) {
      newFilterModel.items.push({
        field: idColumn,
        operator: 'equals',
        value: value.id,
      });
    }

    // setFilterModel if newFilterModel has changed from the current filterModel
    if (filterModelChanged(newFilterModel)) {
      setFilterModel(newFilterModel);
    }
  }, [configuration.defaultFilterModel]);
  useEffect(() => {
    if (configuration.defaultSortModel) {
      setSortModel(configuration.defaultSortModel);
    }
  }, [configuration.defaultSortModel]);

  const options = useMemo(() => {
    const values = listQuery.data?.values ?? [];
    return values.map((row: any) => ({
      id: row[idColumn].toString(),
      label: labelColumns
        ? labelColumns.map((lc) => row[lc]).join(' ')
        : row[idColumn],
    }));
  }, [listQuery.data, idColumn, labelColumns]);

  useEffect(() => {
    if (disabled || listQuery.isFetching || listQuery.isPlaceholderData) return;

    const values = listQuery.data?.values;
    if (!values) return;

    if (value && !options.some((o) => o.id === value.id)) {
      onChange({ target: { value: null } }, null);
    }

    if (
      values.length > 0 &&
      !defaultValueApplied &&
      defaultValueSelector
    ) {
      const defaultValue = defaultValueSelector(values);
      setDefaultValueApplied(true);
      if (defaultValue !== undefined) {
        onChange({ target: { value: defaultValue } }, defaultValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabled,
    listQuery.dataUpdatedAt,
    listQuery.isFetching,
    listQuery.isPlaceholderData,
  ]);

  return (
    <Autocomplete
      fullWidth
      inputValue={inputValue}
      className={className}
      disabled={disabled}
      getOptionLabel={(o: any) => {
        const opFound = options.find((x) => x.id === o.id?.toString());
        let resp = '';
        if (opFound && opFound.id !== opFound.label && opFound.label) {
          resp = `${opFound.id} | ${opFound.label}`;
        } else if (opFound) {
          resp = opFound.id;
        }
        return resp;
      }}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      value={value ?? null}
      loading={loading}
      onChange={onChange}
      onInputChange={handleInputChange}
      options={options}
      renderInput={(params2) => {
        const { InputProps, InputLabelProps, inputProps, ...rest } = params2;
        return (
          <TextField
            {...rest}
            helperText={helperText}
            label={label}
            size={size}
            slotProps={{
              input: {
                ...InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {InputProps?.endAdornment}
                  </>
                ),
              },
              inputLabel: InputLabelProps,
              htmlInput: inputProps,
            }}
          />
        );
      }}
      renderOption={(props, option) => (
        <div key={`${configuration.itemType}-${option.id}`}>
          {(option.id === option.label || !option.label) && (
            <li {...props}>{option.id}</li>
          )}
          {option.id !== option.label && option.label && (
            <li {...props}>
              {option.id} | {option.label}
            </li>
          )}
        </div>
      )}
    />
  );
}

export default AutocompleteField;
